import { useMemo } from 'react';
import { X } from 'lucide-react';
import * as Diff from 'diff';
import { useConfigSnapshotDetail } from '../hooks/useConfigSnapshots';
import { ConfigLine } from './ConfigText';
import { useConfigTextCredentials } from '@/hooks/useCredentialReferences';
import { type DiffLineKind, linesOf } from '@/utils/configDiff';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface ConfigDiffViewerProps {
  deviceId: string;
  preSnapshotId: string;
  postSnapshotId: string;
  onClose?: () => void;
  baseLabel?: string;
  targetLabel?: string;
}

export default function ConfigDiffViewer({
  deviceId,
  preSnapshotId,
  postSnapshotId,
  onClose,
  baseLabel,
  targetLabel,
}: ConfigDiffViewerProps) {
  const { data: preSnapshot, isLoading: preLoading } = useConfigSnapshotDetail(
    deviceId,
    preSnapshotId,
  );
  const { data: postSnapshot, isLoading: postLoading } = useConfigSnapshotDetail(
    deviceId,
    postSnapshotId,
  );

  const diffLines = useMemo(() => {
    if (!preSnapshot || !postSnapshot) return null;
    return linesOf(Diff.diffLines(preSnapshot.configText, postSnapshot.configText));
  }, [preSnapshot, postSnapshot]);
  const credentials = useConfigTextCredentials([preSnapshot?.configText, postSnapshot?.configText]);

  if (preLoading || postLoading) {
    return <p className="text-xs text-muted-foreground">Loading snapshots…</p>;
  }

  if (!preSnapshot || !postSnapshot) {
    return (
      <Alert variant="destructive" className="text-xs">
        <AlertDescription>Could not load snapshot content.</AlertDescription>
      </Alert>
    );
  }

  if (!diffLines) return null;

  const hasChanges = diffLines.some((l) => l.kind !== 'same');
  const added = diffLines.filter((l) => l.kind === 'added').length;
  const removed = diffLines.filter((l) => l.kind === 'removed').length;
  const rotated = diffLines.filter((l) => l.kind === 'rotated').length;

  const preLabel = baseLabel ?? `Pre: ${new Date(preSnapshot.createdAt).toLocaleString()}`;
  const postLabel = targetLabel ?? `Post: ${new Date(postSnapshot.createdAt).toLocaleString()}`;

  return (
    <div className="rounded-md border border-border-subtle bg-card p-3">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <p className="text-xs font-bold">
          Configuration diff
          {hasChanges ? (
            <span className="text-muted-foreground ml-2 font-normal">
              (+{String(added)} / -{String(removed)} lines
              {rotated > 0 ? `, ${String(rotated)} rotated` : ''})
            </span>
          ) : null}
        </p>
        <div className="flex items-center gap-1.5">
          <Badge variant="secondary" className="font-normal text-[10px]">
            {preSnapshot.format}
          </Badge>
          <Badge variant="secondary" className="font-normal text-[10px]">
            {preLabel}
          </Badge>
          <Badge variant="secondary" className="font-normal text-[10px]">
            {postLabel}
          </Badge>
          {onClose ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={onClose}
              aria-label="Close diff viewer"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          ) : null}
        </div>
      </div>

      {!hasChanges ? (
        <p className="text-xs text-muted-foreground">No differences detected.</p>
      ) : (
        <pre className="max-h-96 overflow-auto rounded-md bg-[#1a1a2e] text-[#e0e0e0] p-2 text-xs leading-relaxed font-mono">
          {diffLines.map((l, index) => (
            <span key={index} style={LINE_STYLE[l.kind]} title={l.kind === 'rotated' ? ROTATED_TITLE : undefined}>
              {LINE_PREFIX[l.kind]}
              <ConfigLine line={l.text} credentials={credentials} />
              {'\n'}
            </span>
          ))}
        </pre>
      )}
    </div>
  );
}

const LINE_STYLE: Record<DiffLineKind, React.CSSProperties> = {
  same: { color: '#e0e0e0' },
  added: { color: '#2ecc71', backgroundColor: 'rgba(46, 204, 113, 0.1)' },
  removed: { color: '#e74c3c', backgroundColor: 'rgba(231, 76, 60, 0.1)' },
  rotated: { color: '#f1c40f', backgroundColor: 'rgba(241, 196, 15, 0.08)' },
};

const LINE_PREFIX: Record<DiffLineKind, string> = { same: ' ', added: '+', removed: '-', rotated: '~' };

const ROTATED_TITLE = 'The same credential at a newer version: its secret was rotated, the configuration is otherwise unchanged.';
